"use client";

import { AgenticToolCallRow } from "@/components/ui/agentic-tool-call-row";

export type ToolStep = {
  tool_name: string;
  label?: string;
  tool_label?: string;
  status?: "ok" | "success" | "running" | "error" | "cancelled" | string;
  summary?: string;
  mcp_connector_id?: string;
  step_id?: string;
  kind?: "backend" | "fe" | "mcp";
  /** When set, the row can replay this frontend tool invocation. */
  fe_tool_id?: string;
  fe_tool_params?: Record<string, unknown>;
};

function normalizeToolStepStatus(status?: string): string | undefined {
  if (status === "ok") {
    return "success";
  }
  return status;
}

export function parseToolStep(raw: object | null | undefined): ToolStep | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const tool_name = obj.tool_name;
  if (typeof tool_name !== "string" || !tool_name) return null;
  const fe_tool_id = typeof obj.fe_tool_id === "string" ? obj.fe_tool_id : undefined;
  const rawParams = obj.fe_tool_params;
  const fe_tool_params =
    rawParams && typeof rawParams === "object" && !Array.isArray(rawParams)
      ? (rawParams as Record<string, unknown>)
      : undefined;
  const label =
    typeof obj.label === "string"
      ? obj.label
      : typeof obj.tool_label === "string"
        ? obj.tool_label
        : undefined;
  const tool_label = typeof obj.tool_label === "string" ? obj.tool_label : undefined;
  const rawStatus = typeof obj.status === "string" ? obj.status : undefined;
  return {
    tool_name,
    label,
    tool_label,
    status: normalizeToolStepStatus(rawStatus),
    summary: typeof obj.summary === "string" ? obj.summary : undefined,
    mcp_connector_id:
      typeof obj.mcp_connector_id === "string" ? obj.mcp_connector_id : undefined,
    step_id: typeof obj.step_id === "string" ? obj.step_id : undefined,
    kind:
      obj.kind === "backend" || obj.kind === "fe" || obj.kind === "mcp" ? obj.kind : undefined,
    fe_tool_id,
    fe_tool_params,
  };
}

function mapStatus(status?: string) {
  if (status === "error") return "error" as const;
  if (status === "pending") return "pending" as const;
  if (status === "running") return "running" as const;
  if (status === "cancelled") return "cancelled" as const;
  return "success" as const;
}

function displayLabel(step: ToolStep): string {
  return step.label || step.tool_label || step.tool_name;
}

/** Single-line tool step row — delegates to design-system `AgenticToolCallRow`. */
export function ToolStepRow({
  step,
  onReplayFeTool,
}: {
  step: ToolStep;
  onReplayFeTool?: (toolId: string, params: Record<string, unknown>) => void;
}) {
  const replayable =
    Boolean(step.fe_tool_id) &&
    step.fe_tool_params &&
    typeof onReplayFeTool === "function";

  const handleReplay = () => {
    if (!replayable || !step.fe_tool_id || !step.fe_tool_params) {
      return;
    }
    onReplayFeTool(step.fe_tool_id, step.fe_tool_params);
  };

  return (
    <AgenticToolCallRow
      toolName={step.tool_name}
      label={displayLabel(step)}
      status={mapStatus(step.status)}
      hidePrefix={Boolean(step.kind)}
      summary={step.summary}
      data-testid="chat-tool-step"
      role={replayable ? "button" : undefined}
      tabIndex={replayable ? 0 : undefined}
      title={replayable ? "Replay tool" : undefined}
      className={
        replayable
          ? "cursor-pointer rounded-md !pl-0 hover:bg-slate-100/80 dark:hover:bg-slate-800/50"
          : "!pl-0"
      }
      onClick={replayable ? handleReplay : undefined}
      onKeyDown={
        replayable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleReplay();
              }
            }
          : undefined
      }
    />
  );
}
